import { LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {delay, filter, map, publishReplay, refCount} from 'rxjs/operators';
import { BusinessNavKeybindService } from '../../../keybind.service';

export interface Tab {
  id: string;
  elem: ElementRef;
}

@Injectable({providedIn: 'root'})
export class BusinessNavLocationTabService {
  private selectedTab$$: BehaviorSubject<Tab> = new BehaviorSubject<Tab>(null);
  public selectedTab$: Observable<Tab> = this.selectedTab$$.asObservable().pipe(delay(0)); // delay 0 for the ink bar to be the right width
  public selectedTabElement$: Observable<ElementRef>;
  public selectedTabId$: Observable<string>;
  public selectedTabIndex$: Observable<number>;

  private tabs: Tab[] = [];

  constructor(private keybindService: BusinessNavKeybindService) {
    this.selectedTabId$ = this.selectedTab$.pipe(
      map((tab: Tab) => (tab) ? tab.id : ''),
      publishReplay(1),
      refCount()
    );
    this.selectedTabElement$ = this.selectedTab$.pipe(
      filter((tab: Tab) => tab !== null),
      map((tab: Tab) => tab.elem),
      publishReplay(1),
      refCount()
    );
    this.selectedTabIndex$ = this.selectedTab$.pipe(
      map((tab: Tab) => (tab) ? this.tabs.indexOf(tab) : 0),
      publishReplay(1),
      refCount()
    );

    this.keybindService.keyEvent$.subscribe((event: KeyboardEvent) => {
      const tab = this.selectedTab$$.getValue();
      if (!tab) {
        return;
      }
      const tabId = this.tabs.indexOf(tab);
      // shift + -> to change tabs
      if (event.shiftKey && event.keyCode === LEFT_ARROW) {
        if (tabId > 0) {
          this.selectTab(this.tabs[tabId - 1].id);
        } else if (tabId === 0) {
          this.selectTab(this.tabs[this.tabs.length - 1].id);
        }
      }
      if (event.shiftKey && event.keyCode === RIGHT_ARROW) {
        if (tabId < this.tabs.length - 1) {
          this.selectTab(this.tabs[tabId + 1].id);
        } else if (tabId === this.tabs.length - 1) {
          this.selectTab(this.tabs[0].id);
        }
      }
    });
  }

  private getTabById(id: string): Tab {
    return this.tabs.find((tab: Tab) => tab.id === id);
  }

  public selectTab(tabId: string): void {
    const tab = this.getTabById(tabId);
    if (!tab) {
      console.log('Could not select tab, was it registered?');
      return;
    }
    this.selectedTab$$.next(tab);
  }

  public registerTabIndex(tab: Tab): void {
    this.tabs.push(tab);
  }

  public clear(): void {
    this.tabs = [];
    this.selectedTab$$.next(null);
  }
}
